import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/libs/utils/cn';

const carScoreVariants = cva('relative inline-flex items-center justify-center', {
  variants: {
    size: {
      sm: 'w-8 h-8',
      md: 'w-12 h-12',
      lg: 'w-16 h-16',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const scoreColors = {
  0: {
    fill: '#ef4444', // red-500
    text: '#ffffff',
  },
  1: {
    fill: '#f97316', // orange-500
    text: '#ffffff',
  },
  2: {
    fill: '#eab308', // yellow-500
    text: '#1f2937', // gray-800
  },
  3: {
    fill: '#22c55e', // green-500
    text: '#ffffff',
  },
} as const;

type Score = 0 | 1 | 2 | 3;

interface CarScoreIconProps extends VariantProps<typeof carScoreVariants> {
  score: Score;
  className?: string;
}

export function CarScoreIcon({ score, size = 'md', className }: CarScoreIconProps) {
  const colors = scoreColors[score];

  const fontSize = {
    sm: '0.5rem',
    md: '0.625rem',
    lg: '0.875rem',
  }[size ?? 'md'];

  return (
    <div className={cn(carScoreVariants({ size, className }))}>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full"
        role="img"
        aria-labelledby="car-score-title"
      >
        <title id="car-score-title">Score: {score}</title>
        {/* Car body - exact Lucide CarIcon path, filled */}
        <path
          d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"
          fill={colors.fill}
          stroke={colors.fill}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Axle line between wheels */}
        <path d="M9 17h6" stroke={colors.fill} strokeWidth="2" strokeLinecap="round" />
        {/* Left wheel */}
        <circle cx="7" cy="17" r="2" fill="#374151" stroke="#1f2937" strokeWidth="0.5" />
        <circle cx="7" cy="17" r="0.75" fill="#9ca3af" />
        {/* Right wheel */}
        <circle cx="17" cy="17" r="2" fill="#374151" stroke="#1f2937" strokeWidth="0.5" />
        <circle cx="17" cy="17" r="0.75" fill="#9ca3af" />
        {/* Score number - positioned in the car body */}
        <text
          x="12"
          y="12"
          textAnchor="middle"
          dominantBaseline="middle"
          fill={colors.text}
          fontSize={fontSize}
          fontWeight="bold"
          fontFamily="system-ui, sans-serif"
        >
          {score}
        </text>
      </svg>
    </div>
  );
}
